/* eslint-disable camelcase */
import { useState } from 'react';
import { ApolloError, gql, useMutation } from '@apollo/client';
import Cookies from 'js-cookie';

import Config from 'Config';
import { Cookie } from 'constants/cookies';
import { useNotifications } from 'contexts/notifications/use-notifications';
import { isStateJwtToken, navigateToSelectedProduct } from 'helpers/navigation';
import Location from 'services/Location';
import { getAccessToken } from 'helpers/account';
import { parseJwt } from 'helpers/token';
import { CountdownTimer } from 'routes/stop-screen/countdown-timer/CountdownTimer';

export const SEND_VERIFICATION_CODE = gql`
  mutation SendAccountVerificationCode($jar: JAR) {
    SendAccountVerificationCode(jar: $jar)
  }
`;

type UseSendVerificationCode = {
  sendVerificationCode(triggeredManually?: boolean): void;
  hasBeenTiggeredManually: boolean;
  hasEmailBounced: boolean;
  isProcessing: boolean;
  data: boolean;
};

export const useSendVerificationCode = (email: string): UseSendVerificationCode => {
  const [hasBeenTiggeredManually, setHasBeenTiggeredManually] = useState<boolean>(false);
  const [hasEmailBounced, setHasEmailBounced] = useState<boolean>(false);
  const accessToken = getAccessToken();
  const { showToast } = useNotifications();

  const handleError = (error: ApolloError): void => {
    if (error.message === 'temporarily_unavailable') {
      showToast({
        content: 'You reached the limit of confirmation emails',
        variant: 'error',
        autoHideDelayTime: 600000, // 10min
        action: {
          label: <CountdownTimer onClick={() => handleSendVerification(true)} />,
          onClick: () => {},
        },
      });

      return;
    }

    if (
      error.message === 'invalid_request' &&
      (error.graphQLErrors[0]?.extensions?.description === 'email address inactive' ||
        error.graphQLErrors[0]?.extensions?.description === 'inactive email address')
    ) {
      setHasEmailBounced(true);
      return;
    }

    if (error.message === 'conflict') {
      Cookies.remove(Cookie.VerificationCodeSend);
      showToast({
        content: 'Your email is confirmed',
        variant: 'info',
      });
      navigateToSelectedProduct(email);

      return;
    }

    showToast({
      content: "We couldn't send the verification code",
      variant: 'error',
    });
  };

  const [sendVerificationCode, { data, loading }] = useMutation<boolean>(SEND_VERIFICATION_CODE, {
    context: {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    },
    onError: handleError,
    onCompleted: () => {
      showToast({
        content: 'Confirmation email sent!',
        variant: 'success',
      });
    },
  });

  const handleSendVerification = (triggeredManually?: boolean): void => {
    const state = Location.getHashParam('state');

    if (isStateJwtToken(state)) {
      const token = parseJwt(state);
      const clientID =
        Location.getHashParam('client_id') ||
        Location.getUrlParam('client_id') ||
        token?.client_id ||
        Config.default_client_id;
      sendVerificationCode({ variables: { jar: { request: state, client_id: clientID } } });
    } else {
      sendVerificationCode({ variables: { jar: null } });
    }

    setHasBeenTiggeredManually(triggeredManually);
  };

  return {
    sendVerificationCode: handleSendVerification,
    isProcessing: loading,
    hasBeenTiggeredManually,
    hasEmailBounced,
    data,
  };
};
